import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

const getNow = (tz) => dayjs().tz(tz);

const getDateWithTZ = (dateStr, tz) => dayjs.tz(dateStr, tz);

const getAvailability = (storeOrDays, tzParam, isMenu) => {
  // Handle different input formats (store object or days array)
  let days;
  let tz;

  if (storeOrDays && typeof storeOrDays === 'object' && storeOrDays.storadays) {
    // It's a store object
    const store = storeOrDays;
    days = store.storadays;
    tz = store.timezoneid;
  } else {
    // It's already a days array
    days = storeOrDays;
    tz = tzParam;
  }

  if (!days || !Array.isArray(days) || days.length === 0) {
    return {
      status: 'closed',
      label: 'Closed',
      isAvailable: isMenu,
      startHour: isMenu ? false : null,
      closeHour: isMenu ? false : null,
    };
  }

  const now = getNow(tz);

  const currentDay = now.isoWeekday() - 1;
  let isAvailable = false;
  let startHour = null;
  let closeHour = null;
  let status = 'closed';
  let label = 'Closed';

  // Check previous day rules that extend to current day
  const previousDay = (currentDay - 1 + 7) % 7;
  const previousDayRules = days.filter(
    (rule) =>
      rule.day === previousDay &&
      (rule.endhour < rule.starthour ||
        (rule.endhour === rule.starthour && rule.endmin < rule.startmin))
  );

  for (const rule of previousDayRules) {
    const previousDate = getNow(tz)
      .clone()
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    let startTime = dayjs
      .tz(previousDate, tz)
      .hour(rule.starthour)
      .minute(rule.startmin)
      .second(0);
    let endTime = dayjs
      .tz(previousDate, tz)
      .hour(rule.endhour)
      .minute(rule.endmin)
      .second(0)
      .add(1, 'day');

    if (now.isAfter(startTime) && now.isBefore(endTime)) {
      isAvailable = true;
      startHour = startTime.format('YYYY-MM-DD HH:mm:ss');
      closeHour = endTime.format('YYYY-MM-DD HH:mm:ss');
      status = 'open';
      label = 'Open';
      break;
    }
  }

  // Check current day rules
  if (!isAvailable) {
    const currentDayRules = days.filter((rule) => rule.day === currentDay);

    for (const rule of currentDayRules) {
      let startTime = dayjs
        .tz(now.format('YYYY-MM-DD'), tz)
        .hour(rule.starthour)
        .minute(rule.startmin)
        .second(0);
      let endTime = dayjs
        .tz(now.format('YYYY-MM-DD'), tz)
        .hour(rule.endhour)
        .minute(rule.endmin)
        .second(0);

      if (endTime.isBefore(startTime)) {
        endTime = endTime.clone().add(1, 'day');
      }

      // Check if it's opening soon (within the next hour)
      const oneHourBeforeOpening = startTime.clone().subtract(1, 'hour');

      if (now.isAfter(oneHourBeforeOpening) && now.isBefore(startTime)) {
        // Store opens within the next hour
        const minutesToOpen = startTime.diff(now, 'minutes');
        status = 'opensSoon';
        label = `Opens in ${minutesToOpen} min`;
        isAvailable = false;
        startHour = startTime.format('YYYY-MM-DD HH:mm:ss');
        closeHour = endTime.format('YYYY-MM-DD HH:mm:ss');
        break;
      } else if (now.isAfter(startTime) && now.isBefore(endTime)) {
        // Store is currently open
        isAvailable = true;
        startHour = startTime.format('YYYY-MM-DD HH:mm:ss');
        closeHour = endTime.format('YYYY-MM-DD HH:mm:ss');
        status = 'open';
        label = 'Open';
        break;
      }
    }
  }

  return {
    status,
    label,
    isAvailable,
    startHour,
    closeHour,
  };
};

export default getAvailability;
